import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';

interface DraggableFeedVideoCardProps {
  id: string;
  title: string;
  thumbnail?: string;
  views?: number;
  plays?: number;
  /** Desabilita o drag (ex.: vídeo em processamento/pending) */
  disabled?: boolean;
  /**
   * Aceita nós normais ou uma render-prop que recebe o estado do drag.
   * A render-prop existe para o card interno conseguir anular o onClick
   * enquanto está sendo arrastado (senão um drag mal sucedido navega).
   */
  children: React.ReactNode | ((state: { isDragging: boolean }) => React.ReactNode);
}

/**
 * Wrapper que torna um card de vídeo arrastável para dentro das pastas.
 * Mantém o card visual intacto — apenas adiciona o comportamento de drag.
 */
export function DraggableFeedVideoCard({
  id,
  title,
  thumbnail,
  views = 0,
  plays = 0,
  disabled = false,
  children,
}: DraggableFeedVideoCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `video-${id}`,
    data: {
      type: 'video',
      videoId: id,
      videoTitle: title,
      videoThumbnail: thumbnail,
      videoViews: views,
      videoPlays: plays,
    },
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // touch-none só a partir de lg: no mobile/tablet o grid é de 1-2 colunas e
      // bloquear o touch-action mataria o scroll vertical iniciado sobre um card.
      className={cn(
        'relative lg:touch-none cursor-grab active:cursor-grabbing outline-none',
        isDragging && 'opacity-40 scale-[0.98] transition-transform'
      )}
    >
      {typeof children === 'function' ? children({ isDragging }) : children}
    </div>
  );
}
